import { accessToken } from '@/auth/oidc'

/**
 * Живой канал уведомлений (тикет #49).
 *
 * ## Почему не EventSource
 *
 * Браузерный `EventSource` не умеет ставить заголовки — он шлёт только куки, а токен
 * здесь ходит в `Authorization`. Вариантов было два: положить токен в query-строку,
 * где он осядет в логах прокси и в истории браузера, или читать поток через `fetch`.
 * Второе стоит примерно сорока строк разбора формата и не размазывает секрет по
 * инфраструктуре.
 *
 * Плата за отказ от `EventSource` — переподключение приходится делать самим. Оно всё
 * равно понадобилось бы: штатный автореконнект не умеет обновлять протухший токен.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** Уведомление в том виде, в каком его шлёт сервер. */
export interface LiveNotification {
  id: string
  title: string
  body: string | null
  kind: string
  entityType: string | null
  entityId: string | null
  createdAt: string
  /** Сколько непрочитанных стало — чтобы бейдж не требовал отдельного запроса. */
  unread: number
}

export interface StreamHandlers {
  onNotification: (notification: LiveNotification) => void
  /** Первый кадр соединения: сколько непрочитанных уже накопилось. */
  onUnreadCount: (unread: number) => void
  /** Смена состояния канала — для индикатора «связь есть/нет». */
  onStateChange?: (connected: boolean) => void
  /**
   * Входящий звонок (#122): живое событие, а не уведомление — строки в центре уведомлений у
   * него нет, оно имеет смысл, только пока звонит телефон.
   */
  onIncomingCall?: (call: Record<string, unknown>) => void
}

/**
 * Открывает канал и держит его открытым, переподключаясь при обрывах.
 *
 * Возвращает функцию остановки. Вызывать её обязательно: без этого при размонтировании
 * компонента остаётся живой цикл переподключений, а на горячей перезагрузке они
 * накапливаются, и одно уведомление приходит десять раз.
 */
export function connectNotificationStream(handlers: StreamHandlers): () => void {
  const controller = new AbortController()
  let stopped = false
  // Отступ растёт: сервер, который лежит, не должен получать шквал переподключений от
  // каждой открытой вкладки. Потолок — полминуты, иначе после долгого простоя
  // пользователь ждёт связи дольше, чем готов.
  let backoffMs = 1000

  const run = async () => {
    while (!stopped) {
      try {
        const token = await accessToken()
        const response = await fetch(`${BASE_URL}/notifications/stream`, {
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        })

        if (!response.ok || !response.body) {
          throw new Error(`stream ${response.status}`)
        }

        handlers.onStateChange?.(true)
        backoffMs = 1000

        await readStream(response.body, handlers)

        // Поток закончился штатно — сервер закрывает соединение по таймауту, чтобы
        // переподключение было нашим и предсказуемым, а не сетевым и незаметным.
        handlers.onStateChange?.(false)
      } catch (error) {
        if (stopped || controller.signal.aborted) return
        handlers.onStateChange?.(false)
      }

      if (stopped) return
      await sleep(backoffMs)
      backoffMs = Math.min(backoffMs * 2, 30_000)
    }
  }

  void run()

  return () => {
    stopped = true
    controller.abort()
  }
}

/**
 * Разбирает `text/event-stream`.
 *
 * Кадр — это набор строк `event:`/`data:`, завершённый пустой строкой. Чтение идёт
 * порциями произвольной длины, поэтому хвост, не оканчивающийся переводом строки,
 * остаётся в буфере до следующей порции: иначе разрезанный посередине JSON попадёт в
 * разбор как два обрывка.
 */
async function readStream(body: ReadableStream<Uint8Array>, handlers: StreamHandlers) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) return

    buffer = feed(buffer + decoder.decode(value, { stream: true }), handlers)
  }
}

/**
 * Скармливает буфер разбору и возвращает неполный хвост.
 *
 * Отдельной функцией, потому что это единственное место, где формат может разъехаться,
 * и проверять его надо без сети: сервер режет поток на порции произвольной длины, и
 * граница порции запросто попадает в середину JSON — тогда кадр придёт двумя обрывками.
 */
export function feed(buffer: string, handlers: StreamHandlers): string {
  let rest = buffer
  let boundary = rest.indexOf('\n\n')

  while (boundary !== -1) {
    dispatch(rest.slice(0, boundary), handlers)
    rest = rest.slice(boundary + 2)
    boundary = rest.indexOf('\n\n')
  }

  return rest
}

function dispatch(frame: string, handlers: StreamHandlers) {
  let event = 'message'
  let data = ''

  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) data += line.slice(5).trim()
  }

  if (!data) return

  try {
    const parsed = JSON.parse(data)
    if (event === 'unread-count') handlers.onUnreadCount(parsed.unread ?? 0)
    else if (event === 'notification') handlers.onNotification(parsed as LiveNotification)
    else if (event === 'incoming-call') handlers.onIncomingCall?.(parsed as Record<string, unknown>)
  } catch {
    // Битый кадр роняет один кадр, а не соединение: разорвать канал из-за одного
    // нечитаемого сообщения значит потерять все последующие.
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
